/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import StoreShell from "@/components/store/StoreShell";
import { fetchPublicProduct } from "@/lib/api";
import { addToCart } from "@/lib/storeCart";

function money(minor) { return `₱${((minor || 0) / 100).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`; }

const JEWELRY_FIELDS = [["metal", "Metal"], ["metal_purity", "Purity"], ["gemstone", "Gemstone"], ["gemstone_type", "Gemstone type"], ["carat", "Carat"], ["weight", "Weight"], ["size", "Size"], ["certificate_reference", "Certificate"], ["collection", "Collection"]];

export default function StoreProductDetail() {
  const { slug, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [variantId, setVariantId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    fetchPublicProduct(slug, productId).then((p) => {
      setProduct(p);
      if (p.variants?.length && !variantId) setVariantId(p.variants[0].id);
    }).catch((err) => { if (err.response?.status === 404) setNotFound(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, productId]);

  if (notFound) return <Navigate to={`/store/${slug}`} replace />;
  if (!product) return <StoreShell slug={slug}><p className="text-sm text-[#475569]">Loading…</p></StoreShell>;

  const hasVariants = product.variants?.length > 0;
  const selectedVariant = product.variants?.find((v) => v.id === variantId);
  const unitPrice = product.price_minor + (selectedVariant?.price_adjustment_minor || 0);
  const availability = hasVariants ? selectedVariant?.availability : product.availability;
  const canBuy = availability !== "out_of_stock" && (!hasVariants || variantId);

  const jewelryFields = JEWELRY_FIELDS.filter(([key]) => product[key]);

  return (
    <StoreShell slug={slug}>
      <Link to={`/store/${slug}`} className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#10B981] mb-4"><ArrowLeft size={15} /> Back to shop</Link>
      <div className="grid gap-6 sm:grid-cols-2">
        <div>{product.images?.[0] ? <img src={product.images[0]} alt={product.name} className="w-full rounded-2xl object-cover" /> : <div className="aspect-square rounded-2xl bg-[#F1F5F9]" />}</div>
        <div>
          <h1 className="text-2xl font-bold font-display text-[#0F172A]">{product.name}</h1>
          <p className="mt-2 text-xl font-semibold text-[#0F172A]">{money(unitPrice)}</p>
          {availability === "out_of_stock" && <p className="mt-1 text-sm font-medium text-red-600">Out of stock</p>}
          {availability === "low_stock" && <p className="mt-1 text-sm font-medium text-amber-600">Low stock</p>}
          {product.description && <p className="mt-3 text-sm text-[#475569]">{product.description}</p>}

          {jewelryFields.length > 0 && (
            <dl className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {jewelryFields.map(([key, label]) => (
                <div key={key}><dt className="text-xs uppercase text-[#94A3B8]">{label}</dt><dd className="text-[#0F172A]">{product[key]}</dd></div>
              ))}
            </dl>
          )}

          {hasVariants && (
            <div className="mt-4">
              <label htmlFor="variant-select" className="block text-xs font-medium uppercase text-[#475569] mb-1">Select option</label>
              <select id="variant-select" value={variantId || ""} onChange={(e) => setVariantId(e.target.value)}
                className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white">
                {product.variants.map((v) => (
                  <option key={v.id} value={v.id} disabled={v.availability === "out_of_stock"}>{v.name}{v.availability === "out_of_stock" ? " — Out of stock" : ""}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            <label htmlFor="quantity" className="text-xs font-medium uppercase text-[#475569]">Qty</label>
            <input id="quantity" type="number" min="1" value={quantity} onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))} className="w-20 rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
          </div>

          <button disabled={!canBuy} onClick={() => {
            addToCart(slug, {
              product_id: product.id, variant_id: variantId || null, quantity,
              name: product.name, variant_name: selectedVariant?.name, unit_price_minor: unitPrice, image: product.images?.[0],
            });
            setAdded(true);
          }} className="mt-5 w-full rounded-xl bg-[#0F172A] px-4 py-3 text-sm font-semibold text-white disabled:opacity-40">
            {canBuy ? "Add to Cart" : "Out of Stock"}
          </button>
          {added && (
            <div className="mt-3 flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              Added to cart. <button onClick={() => navigate(`/store/${slug}/cart`)} className="font-semibold underline">View cart</button>
            </div>
          )}
        </div>
      </div>
    </StoreShell>
  );
}
